/**
 * GoTu Admin roles — the display layer for auth-service's `internal_role`.
 *
 * The slugs mirror `validRoles` in auth-service
 * (internal/model/internaluser/internal_user.go). auth-service has no endpoint
 * that enumerates them, so the set is maintained by hand here and pinned by
 * internalRoles.test.js; `labelForRole` covers the gap in the meantime, since a
 * role reaches the column via a Google Workspace group and can therefore appear
 * in prod before anyone edits this file.
 *
 * The labels have no upstream source at all: auth-service stores and releases
 * slugs, so display strings are ours either way.
 */

export const INTERNAL_ROLES = [
  'general',
  'superadmin',
  'finance',
  'legal',
  'marketing',
  'sales_admin',
  'ae',
  'recruiter',
  'dso',
  'ram',
  'ram_manager',
  'rae',
  'rae_manager',
  'cs_admin',
  'cs_ops_t1_t2',
  'cs_ops_t3',
];

export const INTERNAL_ROLE_LABELS = {
  general: 'General',
  superadmin: 'Super Admin',
  finance: 'Finance',
  legal: 'Legal',
  marketing: 'Marketing',
  sales_admin: 'Sales Admin',
  ae: 'AE',
  recruiter: 'Recruiter',
  dso: 'DSO',
  ram: 'RAM',
  ram_manager: 'RAM Manager',
  rae: 'RAE',
  rae_manager: 'RAE Manager',
  cs_admin: 'CS Admin',
  cs_ops_t1_t2: 'CS Ops T1/T2',
  cs_ops_t3: 'CS Ops T3',
};

/** Shown wherever a value is absent, matching domain/Campaigns' EMPTY_VALUE. */
export const EMPTY_VALUE = '—';

/**
 * Shown when the role could not be determined at all — the `/me` lookup 404'd
 * (this admin holds no internal_user row) or the request failed.
 *
 * Deliberately not EMPTY_VALUE: an em dash reads as "this admin has no role",
 * which is a claim we cannot make, and `internal_role` is NOT NULL upstream so
 * it is never true. A 404 here means a provisioning gap, and the homepage card
 * exists to make exactly that kind of gap visible when someone reports an
 * access problem.
 */
export const UNKNOWN_ROLE_LABEL = 'Unknown';

// "ram_manager" -> "Ram Manager". Deliberately not clever about acronyms: this
// only runs for roles we have not seen, where the point is legibility, not
// polish. Anything that renders badly here should get a real label above.
const titleCaseToken = (value) =>
  String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * The display name for a stored `internal_role`.
 *
 * @param {string} [role] - The slug as auth-service stored it.
 * @returns {string} Declared label, else a title-cased slug, else an em dash.
 */
export const labelForRole = (role) => {
  if (role === null || role === undefined || role === '') return EMPTY_VALUE;

  return INTERNAL_ROLE_LABELS[role] ?? titleCaseToken(role);
};

/**
 * Options for the role filter, `{ value, label }` as MultiSelect expects.
 *
 * Sorted by label: MultiSelect renders in the order given, and sixteen roles
 * only scan sorted the way they read rather than by slug.
 */
export const INTERNAL_ROLE_FILTER_OPTIONS = INTERNAL_ROLES.map((role) => ({
  value: role,
  label: labelForRole(role),
})).sort((a, b) => a.label.localeCompare(b.label));
